import React from "react";
import { AlertCircle, Inbox, Loader2, RefreshCw } from "lucide-react";
import Button from "./Button";

export interface Column<T> {
    key: string;
    header: string;
    render: (row: T) => React.ReactNode;
    className?: string;
    /** hide on small screens */
    hideOnMobile?: boolean;
}

interface DataTableProps<T> {
    columns: Column<T>[];
    rows: T[] | null;
    loading?: boolean;
    error?: string;
    onRetry?: () => void;
    rowKey: (row: T) => string | number;
    onRowClick?: (row: T) => void;
    emptyTitle?: string;
    emptyText?: string;
    emptyAction?: React.ReactNode;
}

export default function DataTable<T>({
    columns,
    rows,
    loading,
    error,
    onRetry,
    rowKey,
    onRowClick,
    emptyTitle = "Nothing here yet",
    emptyText,
    emptyAction,
}: DataTableProps<T>) {
    if (loading && !rows) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="text-[#8b5cf6] animate-spin" size={32} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center px-6">
                <div className="p-3 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 mb-3">
                    <AlertCircle size={22} />
                </div>
                <p className="text-slate-300 text-sm">{error}</p>
                {onRetry && (
                    <Button variant="secondary" size="sm" icon={RefreshCw} onClick={onRetry} className="mt-4">
                        Retry
                    </Button>
                )}
            </div>
        );
    }

    if (!rows || rows.length === 0) {
        return <EmptyState title={emptyTitle} text={emptyText} action={emptyAction} />;
    }

    return (
        <div className={`overflow-x-auto transition-opacity ${loading ? "opacity-60" : ""}`}>
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-slate-800">
                        {columns.map((c) => (
                            <th
                                key={c.key}
                                className={`text-left text-[10px] font-bold text-slate-500 uppercase tracking-[0.15em] px-5 py-3.5 whitespace-nowrap ${
                                    c.hideOnMobile ? "hidden md:table-cell" : ""
                                } ${c.className ?? ""}`}
                            >
                                {c.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr
                            key={rowKey(row)}
                            onClick={onRowClick ? () => onRowClick(row) : undefined}
                            className={`border-b border-slate-800/60 last:border-0 transition-colors ${
                                onRowClick ? "cursor-pointer hover:bg-[#8b5cf6]/5" : "hover:bg-slate-800/20"
                            }`}
                        >
                            {columns.map((c) => (
                                <td
                                    key={c.key}
                                    className={`px-5 py-3.5 text-slate-300 align-middle ${c.hideOnMobile ? "hidden md:table-cell" : ""} ${c.className ?? ""}`}
                                >
                                    {c.render(row)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function EmptyState({ title, text, action }: { title: string; text?: string; action?: React.ReactNode }) {
    return (
        <div className="flex flex-col items-center justify-center py-16 text-center px-6">
            <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-800 text-slate-500 mb-4">
                <Inbox size={26} />
            </div>
            <p className="text-white font-semibold">{title}</p>
            {text && <p className="text-slate-500 text-sm mt-1 max-w-sm">{text}</p>}
            {action && <div className="mt-5">{action}</div>}
        </div>
    );
}

/** Stops row-click navigation when clicking buttons inside a row */
export function RowActions({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            {children}
        </div>
    );
}